import { useCallback, useEffect, useState } from 'react';
import { apiClient } from '../../../auth-lib';
import { Button, Card, Input, Modal, Skeleton, useToast } from '../../../design-system';
import { LEAVE_STATUS, fmtRange } from '../../../components/attendance/monthOverlay';
import '../../styles/Attendance.css';

// Leave waiting to be decided.
//
// The applicant's BALANCE sits on the row, because that is the number that
// actually decides the request. Sending a manager to another screen to find out
// whether somebody has days left means they approve without looking.

const FILTERS = [
  { key: 'pending', label: 'To decide' },
  { key: 'approved', label: 'Approved' },
  { key: 'rejected', label: 'Rejected' },
  { key: 'cancelled', label: 'Withdrawn' },
];

function LeaveQueue({ onChanged }) {
  const toast = useToast();
  const [status, setStatus] = useState('pending');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);
  // The leave being ended, and from when. Null when the dialog is shut.
  const [ending, setEnding] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    apiClient.get('/api/attendance/leave/requests', { params: { status } })
      .then((r) => setData(r.data))
      .catch((e) => toast.danger(e.response?.data?.error || 'Could not load leave requests'))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status]);
  useEffect(load, [load]);

  const todayKey = () => new Date().toLocaleDateString('en-CA');

  // ⚠️ THE DEFAULT CUT-OFF IS TODAY, NOT THE LEAVE'S FIRST DAY. For leave
  // already running, the days before today were genuinely taken — defaulting to
  // the start would hand them back to the balance and wipe them off the
  // register with one unconsidered click. Leave that has not begun defaults to
  // its own start, which cancels the whole thing.
  const openEnd = (r) => {
    const today = todayKey();
    setEnding({ r, from: r.fromDate > today ? r.fromDate : today, reason: '' });
  };

  const endLeave = async () => {
    const { r, from, reason } = ending;
    setBusy(r.id);
    try {
      const { data: res } = await apiClient.post(`/api/attendance/leave/${r.id}/cancel`, {
        effectiveFrom: from,
        reason: reason.trim(),
      });
      const whole = res.request.status === 'cancelled';
      toast.success(whole
        ? `${r.userName}'s ${r.leaveTypeName} cancelled — they have been told`
        : `${r.userName} is expected in from ${from} · ${res.dropped} day(s) dropped`);
      setEnding(null);
      load();
      onChanged?.();
    } catch (e) {
      toast.danger(e.response?.data?.error || 'Could not cancel that leave');
    } finally {
      setBusy(null);
    }
  };

  const decide = async (r, action) => {
    let note = '';
    if (action === 'reject') {
      // eslint-disable-next-line no-alert
      note = window.prompt(`Why is ${r.userName}'s ${r.leaveTypeName} being refused?\nThey see this.`) || '';
      if (!note.trim()) return;
    }
    setBusy(r.id);
    try {
      const res = await apiClient.post(`/api/attendance/leave/${r.id}/${action}`, { note });
      if (action === 'approve' && res.data.overQuota) {
        // Approving past quota is allowed — the manager is the person entitled
        // to make that exception — but it must not happen silently.
        toast.warning?.(`Approved. ${r.userName} is now over their ${r.leaveTypeName} quota.`)
          || toast.success(`Approved — note that ${r.userName} is now over quota.`);
      } else {
        toast.success(action === 'approve' ? 'Approved' : 'Rejected — they have been told why');
      }
      load();
      onChanged?.();
    } catch (e) {
      toast.danger(e.response?.data?.error || 'That did not work');
    } finally {
      setBusy(null);
    }
  };

  const rows = data?.rows || [];

  return (
    <Card>
      <div className="att-head">
        <div>
          <h3 className="att-head-title">Leave requests</h3>
          <p className="att-head-sub">
            Nothing appears on anyone&apos;s register until it is approved. Approving is what makes
            the day stop counting as an absence.
          </p>
        </div>
      </div>

      <div className="fin-filter-row">
        {FILTERS.map((f) => (
          <button
            key={f.key}
            type="button"
            className={`fin-chip${status === f.key ? ' is-active' : ''}`}
            onClick={() => setStatus(f.key)}
          >
            {f.label}
            {f.key === 'pending' && data?.pendingCount > 0 && ` · ${data.pendingCount}`}
          </button>
        ))}
      </div>

      {loading ? <Skeleton.Text lines={5} /> : rows.length === 0 ? (
        <p className="att-empty">
          {status === 'pending' ? 'Nothing waiting — you are up to date.' : 'Nothing here.'}
        </p>
      ) : (
        <ul className="att-lv-list">
          {rows.map((r) => {
            const s = LEAVE_STATUS[r.status];
            const b = r.balance;
            // What their balance WOULD be if this is approved — the number the
            // decision actually turns on.
            const after = b && !b.unlimited ? Math.round((b.remaining - r.dayCount) * 10) / 10 : null;
            return (
              <li className="att-lv" key={r.id}>
                <span className={`att-lv-s ${s.cls}`}>{s.label}</span>
                <span className="att-lv-main">
                  <span className="att-lv-when">
                    {r.userName} · {r.leaveTypeName} · {fmtRange(r.fromDate, r.toDate)}
                  </span>
                  <span className="att-lv-meta">
                    {r.dayCount} day{r.dayCount === 1 ? '' : 's'}
                    {b && (b.unlimited
                      ? ' · unlimited'
                      : ` · ${b.remaining} of ${b.quota} left${
                        after !== null ? `, ${after} after this` : ''}`)}
                    {b && !b.unlimited && after !== null && after < 0 && (
                      <b className="att-neg"> · over quota</b>
                    )}
                  </span>
                  {r.reason && <span className="att-lv-reason">{r.reason}</span>}
                  {r.decisionNote && (
                    <span className="att-lv-reason"><b>{r.decidedByName}:</b> {r.decisionNote}</span>
                  )}
                  {r.cancelledByName && (
                    <span className="att-lv-reason">
                      <b>Ended by {r.cancelledByName}</b>
                      {r.cancelledFrom ? ` from ${r.cancelledFrom}` : ''}
                      {r.cancelReason ? ` — ${r.cancelReason}` : ''}
                    </span>
                  )}
                </span>
                <span className="att-lv-actions">
                  {r.status === 'pending' && (
                    <>
                      <Button size="sm" variant="primary" disabled={busy === r.id}
                        onClick={() => decide(r, 'approve')}>Approve</Button>
                      <Button size="sm" variant="ghost" disabled={busy === r.id}
                        onClick={() => decide(r, 'reject')}>Reject</Button>
                    </>
                  )}
                  {/* ⚠️ APPROVED LEAVE TOO, which is the whole point of this
                      control. Plans change after a grant — that is ordinary,
                      and until now the only route was a manager editing the
                      register by hand, which leaves the leave ledger still
                      saying they are off. */}
                  {r.status === 'approved' && (
                    <Button size="sm" variant="ghost" disabled={busy === r.id}
                      onClick={() => openEnd(r)}>Cancel leave</Button>
                  )}
                </span>
              </li>
            );
          })}
        </ul>
      )}

      {ending && (
        <Modal
          isOpen
          onClose={() => setEnding(null)}
          title={`End ${ending.r.userName}'s ${ending.r.leaveTypeName}`}
          size="sm"
          footer={
            <>
              <Button variant="secondary" onClick={() => setEnding(null)}
                disabled={busy === ending.r.id}>Keep it</Button>
              <Button variant="primary" onClick={endLeave} loading={busy === ending.r.id}>
                {ending.from <= ending.r.fromDate ? 'Cancel the leave' : 'End it from this date'}
              </Button>
            </>
          }
        >
          <p className="att-hint" style={{ marginTop: 0 }}>
            Approved for {fmtRange(ending.r.fromDate, ending.r.toDate)} — {ending.r.dayCount}
            {ending.r.dayCount === 1 ? ' day' : ' days'}.
          </p>

          <Input
            label="No longer on leave from"
            type="date"
            value={ending.from}
            min={ending.r.fromDate}
            max={ending.r.toDate}
            onChange={(e) => setEnding((x) => ({ ...x, from: e.target.value }))}
            helperText={ending.from <= ending.r.fromDate
              ? 'The whole request is cancelled and the days go back to their balance.'
              : `Days before ${ending.from} stay on the register and still count against their balance.`}
          />

          <Input
            label="Why (they see this)"
            placeholder="e.g. Returned early from Diwali leave"
            value={ending.reason}
            onChange={(e) => setEnding((x) => ({ ...x, reason: e.target.value.slice(0, 300) }))}
          />

          {/* ⚠️ THE CONSEQUENCE, STATED. The person planned around this day
              being off; the one thing that must not happen is them finding out
              on the morning itself. */}
          <p className="att-hint">
            {ending.r.userName} is told straight away, in their notification bell and on any
            device where they have alerts turned on.
          </p>
        </Modal>
      )}
    </Card>
  );
}

export default LeaveQueue;
